import createTheme from "@mui/material/styles/createTheme";
import ThemeProvider from "@mui/material/styles/ThemeProvider";

declare module "@mui/material/styles" {
  interface Palette {
    gray: Palette["primary"];
    errorColor: Palette["primary"];
    darkGray: Palette["primary"];
    cyan: Palette["primary"];
    purple: Palette["primary"];
    dirtyYellow: Palette["primary"];
    brown: Palette["primary"];
    dirtyBlue: Palette["primary"];
    dirtyGreen: Palette["primary"];
  }

  interface PaletteOptions {
    gray: PaletteOptions["primary"];
    errorColor: PaletteOptions["primary"];
    darkGray: PaletteOptions["primary"];
    cyan: PaletteOptions["primary"];
    purple: PaletteOptions["primary"];
    dirtyYellow: PaletteOptions["primary"];
    brown: PaletteOptions["primary"];
    dirtyBlue: PaletteOptions["primary"];
    dirtyGreen: PaletteOptions["primary"];
  }
}

declare module "@mui/material/Button" {
  interface ButtonPropsColorOverrides {
    gray: true;
    errorColor: true;
    darkGray: true;
    cyan: true;
    purple: true;
    dirtyYellow: true;
    brown: true;
    dirtyBlue: true;
    dirtyGreen: true;
  }
}

const fontFamily = [
  '"Inter Variable"',
  "Inter",
  "-apple-system",
  "Segoe UI",
  "Helvetica Neue",
  "Arial",
  "sans-serif",
].join(",");

const border = "#e6e8f0";
const inputBorder = "#c5cad8";
const inputBorderHover = "#9aa1b8";
const textPrimary = "#1b1e33";
const textSecondary = "#6b7192";

let theme = createTheme({
  shape: { borderRadius: 8 },
  typography: {
    fontFamily,
    h1: { fontSize: "2rem", fontWeight: 700, letterSpacing: "-0.02em" },
    h2: { fontSize: "1.625rem", fontWeight: 700, letterSpacing: "-0.02em" },
    h3: { fontSize: "1.375rem", fontWeight: 600, letterSpacing: "-0.01em" },
    h4: { fontSize: "1.25rem", fontWeight: 600, letterSpacing: "-0.01em" },
    h5: { fontSize: "1.0625rem", fontWeight: 600 },
    h6: { fontSize: "0.9375rem", fontWeight: 600 },
    subtitle1: { fontSize: "0.9375rem", fontWeight: 500 },
    subtitle2: { fontSize: "0.8125rem", fontWeight: 600, color: textSecondary },
    body1: { fontSize: "0.9375rem" },
    body2: { fontSize: "0.875rem" },
    caption: { fontSize: "0.75rem", color: textSecondary },
    // Sentence case reads as considered; the MUI default shouts in all caps.
    button: { fontSize: "0.875rem", fontWeight: 600, textTransform: "none" },
  },
  components: {
    MuiButtonBase: {
      defaultProps: { disableRipple: false },
      styleOverrides: {
        root: {
          "&:focus-visible": {
            outline: `2px solid ${textPrimary}`,
            outlineOffset: 2,
          },
        },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          minHeight: "36px",
          padding: "6px 14px",
          minWidth: "88px",
          borderRadius: "8px",
          whiteSpace: "nowrap",
          lineHeight: 1.4,
          "&:focus-visible": {
            outline: "2px solid #ED633A",
            outlineOffset: 2,
          },
        },
        sizeSmall: {
          minHeight: "30px",
          padding: "4px 10px",
          minWidth: "64px",
          fontSize: "0.8125rem",
        },
        sizeLarge: { minHeight: "42px", padding: "8px 18px" },
        outlined: { borderColor: border },
      },
    },
    // navigation
    MuiAppBar: {
      styleOverrides: {
        root: { backgroundColor: "#FFFFFF", color: textPrimary },
      },
    },
    // attorneys card
    MuiCard: {
      styleOverrides: {
        root: {
          marginTop: "24px",
          borderRadius: "12px",
          border: `1px solid ${border}`,
          boxShadow: "0 1px 2px rgba(27, 30, 51, 0.04)",
        },
      },
    },
    MuiCardActions: {
      styleOverrides: {
        root: {
          justifyContent: "center",
          backgroundColor: "#fafbfd",
          borderTop: `1px solid ${border}`,
        },
      },
    },
    // popup dialog
    MuiDialog: {
      styleOverrides: {
        root: { textAlign: "center" },
        paper: { borderRadius: "14px" },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: { justifyContent: "center", gap: "8px" },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: { borderColor: border, fontSize: "0.875rem" },
        head: {
          fontWeight: 600,
          color: textSecondary,
          fontSize: "0.75rem",
          letterSpacing: "0.04em",
          textTransform: "uppercase",
          whiteSpace: "nowrap",
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          "&:hover > .MuiTableCell-body": { backgroundColor: "#fafbfd" },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: { minHeight: "44px", borderBottom: `1px solid ${border}` },
        indicator: { height: "2px", borderRadius: "2px" },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          minHeight: "44px",
          padding: "0 16px",
          fontWeight: 600,
          fontSize: "0.875rem",
          textTransform: "none",
          color: textSecondary,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { fontWeight: 600, borderRadius: "6px" },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: "#FFFFFF",
          borderRadius: "8px",
          alignItems: "center",
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: inputBorderHover,
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: "#ED633A",
            borderWidth: "1.5px",
          },
          "&.Mui-error .MuiOutlinedInput-notchedOutline": {
            borderColor: "#d32f2f",
          },
          // Disabled wrapper fieldsets must not strip outlined borders
          "&.Mui-disabled .MuiOutlinedInput-notchedOutline": {
            borderColor: inputBorder,
          },
          "&.MuiInputBase-multiline": {
            alignItems: "flex-start",
            padding: "8px 12px",
          },
        },
        notchedOutline: {
          borderColor: inputBorder,
          borderWidth: "1.5px",
        },
        input: {
          fontSize: "0.875rem",
          lineHeight: 1.4375,
          // Match MUI outlined medium vertical rhythm so placeholder/text center
          padding: "10px 12px",
          height: "1.4375em",
          boxSizing: "content-box",
        },
        inputSizeSmall: {
          // MUI small outlined default — keeps label + placeholder vertically centered
          padding: "8.5px 12px",
          height: "1.4375em",
          boxSizing: "content-box",
        },
        multiline: {
          padding: 0,
        },
        inputMultiline: {
          height: "auto",
          padding: 0,
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontSize: "0.875rem",
          lineHeight: 1.4375,
        },
        sizeSmall: {
          fontSize: "0.875rem",
          lineHeight: 1.4375,
        },
        outlined: {
          // Keep floating label aligned with denser horizontal padding
          "&.MuiInputLabel-sizeSmall:not(.MuiInputLabel-shrink)": {
            transform: "translate(12px, 9px) scale(1)",
          },
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          fontSize: "0.875rem",
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          fontSize: "0.75rem",
          marginTop: 4,
          marginLeft: 2,
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        size: "small",
      },
    },
    MuiFormControl: {
      defaultProps: {
        size: "small",
      },
    },
    MuiSelect: {
      defaultProps: {
        size: "small",
      },
    },
    MuiPaper: {
      styleOverrides: {
        rounded: { borderRadius: "12px" },
      },
    },
  },
  palette: {
    text: { primary: textPrimary, secondary: textSecondary },
    divider: border,
    primary: {
      main: "#ED633A",
      contrastText: "#FFFFFF",
    },
    // Soft neutral secondary — peach kept only as light tint for rare accents.
    secondary: {
      main: "#6b7192",
      light: "#8a90ad",
      dark: "#4f5470",
      contrastText: "#FFFFFF",
    },
    // MUI's stock success/warning/info are cool blues and greens that fight the
    // warm brand orange, so they are restated here in the same tonal family.
    success: {
      main: "#2E8B62",
      light: "#3EA377",
      dark: "#256F4E",
      contrastText: "#FFFFFF",
    },
    warning: {
      main: "#C67C1E",
      light: "#DB9231",
      dark: "#A26418",
      contrastText: "#FFFFFF",
    },
    info: {
      main: "#4A6FA5",
      light: "#5B84BF",
      dark: "#3A5883",
      contrastText: "#FFFFFF",
    },
    gray: {
      main: "#E4E4E4",
      light: "#F0F0F0",
      dark: "#C9C9C9",
      contrastText: "#717171",
    },
    errorColor: {
      main: "#D32F2F",
      light: "#E03131",
      dark: "#BA2929",
      contrastText: "#FFFFFF",
    },
    darkGray: {
      main: "#878787",
      light: "#949494",
      dark: "#6E6E6E",
      contrastText: "#FFFFFF",
    },
    cyan: {
      main: "#A0CFBF",
      light: "#A9DBCA",
      dark: "#8BB5A7",
      contrastText: "#FFFFFF",
    },
    purple: {
      main: "#A294CE",
      light: "#AC9EDB",
      dark: "#70678F",
      contrastText: "#FFFFFF",
    },
    dirtyYellow: {
      main: "#B6B020",
      light: "#C1BE5D",
      dark: "#9C971C",
      contrastText: "#FFFFFF",
    },
    brown: {
      main: "#AE9685",
      light: "#BAA08D",
      dark: "#947F70",
      contrastText: "#FFFFFF",
    },
    dirtyBlue: {
      main: "#7488A0",
      light: "#7D93AD",
      dark: "#617287",
      contrastText: "#FFFFFF",
    },
    dirtyGreen: {
      main: "#6CB245",
      light: "#73BF4B",
      dark: "#5C993C",
      contrastText: "#FFFFFF",
    },
  },
});

theme = createTheme(theme, {
  components: {
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: "#f6f7fb",
          "& .MuiTableCell-head": {
            borderBottom: `1px solid ${theme.palette.divider}`,
          },
        },
      },
    },
  },
});

export default function ThemeManager(props) {
  const { children } = props;
  
  return <ThemeProvider theme={theme}>{children}</ThemeProvider>;
}
